import { get, post, put } from '@/lib/api/client'
import {
  PaginationParamsSchema,
  PublicationFilterParamsSchema,
  type Publication,
  type PublicationDetail,
  type PublicationFilterParams,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '@/lib/api/types'

export class PublicationService {
  /**
   * Filter publications with pagination (Public endpoint)
   * POST /publications/public/filter?page={page}&size={size}
   */
  async getPublications(
    params: PaginationParams ,
    filters: PublicationFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Publication>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = PublicationFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<Publication>>>(
      `publications/public/filter?${query}`,
      validatedFilters
    )
  }

  /**
   * Search publications (Public endpoint)
   * GET /publications/public/search?page={page}&size={size}&searchBy={searchTerm}
   */
  async searchPublications(
    searchTerm: string,
    params: PaginationParams
  ): Promise<ApiResponse<PaginatedResponse<Publication>>> {
    const validatedParams = PaginationParamsSchema.parse(params)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await get<ApiResponse<PaginatedResponse<Publication>>>(
      `publications/public/search?${query}&searchBy=${encodeURIComponent(searchTerm)}`
    )
  }

  /**
   * Get publication by ID (Public endpoint)
   * GET /publications/public/{publicationId}
   */
  async getPublicationById(publicationId: string): Promise<ApiResponse<PublicationDetail>> {
    return await get<ApiResponse<PublicationDetail>>(
      `publications/public/${publicationId}`
    )
  }

  /**
   * Increment view count for a publication
   * PUT /publications/public/{publicationId}/increment-view
   */
  async incrementView(publicationId: string): Promise<ApiResponse<void>> {
    return await put<ApiResponse<void>>(
      `publications/public/${publicationId}/increment-view`
    )
  }

  /**
   * Increment download count for a publication
   * PUT /publications/public/{publicationId}/increment-download
   */
  async incrementDownload(publicationId: string): Promise<ApiResponse<void>> {
    return await put<ApiResponse<void>>(
      `publications/public/${publicationId}/increment-download`
    )
  }

  /**
   * Get home page highlights
   * GET /publications/public/highlights?type=HOME_PAGE_KEY_RESOURCES
   */
  async getHomePageHighlights(): Promise<ApiResponse<Publication[]>> {
    return await get<ApiResponse<Publication[]>>(
      'publications/public/highlights?type=HOME_PAGE_KEY_RESOURCES'
    )
  }
}

// Export singleton instance
export const publicationService = new PublicationService()

// Export default
export default publicationService

